import Backbone from "backbone"
import _ from "underscore"
import {
    formatDate,
    formatNote,
    parseDate,
    replacePhoneNumbers,
} from 'util/twistle'

import Contact from 'app/models/contact'
import WorkflowDefinition from 'app/models/workflow_definition'

const WorkflowExecution = Backbone.Model.extend({
    initialize: function (attribs)
    {
        this.updateAttributes();
    },

    updateAttributes: function ()
    {
        var self = this, data = self.attributes;

        if(data.postProcessed)
        {
            // prevent this from running multiple times
            return;
        }

        data.postProcessed = true;

        if(data.workflowDef?.workflow_activity_steps && !data.workflowDef.has_sub_workflows_on_tracker)
        {
            // use supplied augmented workflow def if it has step data and doesn't show sub-workflow steps
            // on the tracker
            data.workflow_definition = data.workflowDef;
        }
        else if(!data.workflow_definition.isAugmentedModel)
        {
            // augment it if is has not already been augmented
            data.workflow_definition = new WorkflowDefinition(data.workflow_definition).toJSON();
        }

        data.initiatedDate = formatDate(data.adddate, undefined, "DEFAULT_24");

        data.startDisplayDate = data.initiatedDate;

        data.executes_relative_to_target_date = data.workflow_definition.executes_relative_to_target_date;
        data.target_date_label = data.workflow_definition.target_date_label;

        data.start_on_date = data.workflow_definition.start_on_date;
        data.start_date_label = data.workflow_definition.start_date_label;

        data.tracker_data_form_definition = data.workflow_definition.tracker_data_form_definition;
        data.tracker_data_form_definition_title = data.workflow_definition.tracker_data_form_definition_title;

        if(data.executes_relative_to_target_date && data.target_date) {
            data.executionTargetDisplayDate = formatDate(data.target_date,
                                                                      undefined,
                                                                      "DEFAULT_24");
        }

        if(data.start_on_date && data.start_date)
        {
            data.startDateDisplayDate = formatDate(data.start_date,
                                                                undefined,
                                                                "DEFAULT_24");
        }

        if(data.target_actor_account && !data.target_actor_account.id)
        {
            var target_actor_data = _.findWhere(data.accounts, {id:data.target_actor_account});
            if(target_actor_data)
            {
                data.target_actor_account = target_actor_data;
            }
        }

        if (data.target_actor_account) {
            data.target_actor_account = new Contact(data.target_actor_account).toJSON();
        }

        if(data.created_by)
        {
            data.created_by_account = _.findWhere(data.accounts, {id:data.created_by});
        }

        if(data.terminated && data.terminated_date)
        {
            data.terminated_display_date = formatDate(data.terminated_date);
        }

        data.showId = App.account.get("user_admin"); // user admins see IDs in the UI
        data.can_modify = !data.complete;
        data.can_terminate = !data.complete && (!data.parent_execution_activity_step ||
                                                data.parent_execution_activity_step.length === 0 ||
                                                data.workflow_definition.prevent_cascading_termination ||
                                                data.parent_execution_complete);
        data.can_pause = !data.complete && !data.paused && !data.workflow_definition?.hide_pause;
        data.can_resume = !data.complete && data.paused;

        if (data.info_card?.length > 0) {
            data.info_card_formatted = self.formatInfoCard(data.info_card);
        }

        if (data.target_actor_info_card?.length > 0) {
            data.target_actor_info_card_formatted = self.formatInfoCard(data.target_actor_info_card);
        }

        data.step_history = [];

        // only parse this once
        if (!data.execution_props || typeof(data.execution_props) !== 'object') {
            data.execution_props = JSON.parse(data.execution_props || "{}");
        }

        // look for a recorded completion date, if applicable
        if(data.complete && data.execution_props?.workflow_completion_date){
            data.completion_display_date = formatDate(data.execution_props.workflow_completion_date, undefined, "DEFAULT_24");
        }

        // bring in the modication log, but account information is only available when viewing a single execution
        // (e.g. on the ATC we skip this)
        if (data.execution_props.modification_log && data.execution_props.modification_log.length > 0 && data.accounts) {
            _.each(data.execution_props.modification_log, function (mod) {
                if (mod.updated_by_username && data.accounts && data.accounts[mod.updated_by_username]) {
                    mod.modifiedDate = formatDate(mod.updated_on, undefined, "DEFAULT_24");
                    mod.modifiedByAccount = data.accounts[mod.updated_by_username];
                }
                else if (mod.paused_by_username && data.accounts && data.accounts[mod.paused_by_username]) {
                    mod.pausedDate = formatDate(`${mod.paused_on}Z`, undefined, "DEFAULT_24");
                    mod.pausedByAccount = data.accounts[mod.paused_by_username];
                    if(mod.auto_resume_on){
                        mod.pausedAutoResumeDate = formatDate(`${mod.auto_resume_on}Z`, undefined, "DEFAULT_24");
                    }
                }
                else if (mod.resumed_by_username && data.accounts && data.accounts[mod.resumed_by_username]) {
                    mod.resumedDate = formatDate(`${mod.resumed_on}Z`, undefined, "DEFAULT_24");
                    mod.resumedByAccount = data.accounts[mod.resumed_by_username];
                }
                else if (mod.terminated_by_username) {
                    // extract termination reason for separate display
                    data.terminated_reason = mod.reason;
                }
            });
        }

        var formatDisplayDate = function(date) {
            return formatDate(date, undefined, "DEFAULT_24");
        };

        // unpack any nested sub workflow execution steps into a big map
        let subWorkflowSteps = {};
        Object.entries(data.execution_activity_steps).forEach(es => {
            // find the parent activity step (which allows us to select the workflow definition for the sub-workflow execution)
            let parentActivityStep = data.workflow_definition.workflow_activity_steps.find(s => s.id === es[1].workflow_activity_step);
            if(!parentActivityStep){
                return;
            }
            // since a parent step can launch multiple sub-workflows, track them keyed by execution id
            subWorkflowSteps[parentActivityStep.id] = {}
            Object.entries(es[1]?.child_workflow_executions || {}).forEach(subEx => {
                if(!subEx[1]){
                    return;
                }
                if(!subEx[1].execution_activity_steps){
                    // missing execution steps typically indicates we hit a max nested depth
                    subEx[1].omit_execution_activity_steps = true;
                    subEx[1].execution_activity_steps = {};
                    return;
                }
                // now store each subworkflow step (and a reference to the workflow definition) in the map
                Object.entries(subEx[1].execution_activity_steps).forEach(subStep => {
                    // If this is a subworkflow launched from an action, we may have to dig into the tracker steps to find the workflow definition
                    let subTrackerStep = data.workflow_definition.tracker_steps.find(s => s.id === subStep[1].workflow_activity_step),
                        subWFDef = null;

                    if (parentActivityStep && parentActivityStep.workflow_definition_id) {
                        subWFDef = data.workflow_definition.workflow_workflow_definitions[parentActivityStep.workflow_definition_id]
                    } else if (subTrackerStep && subTrackerStep.workflow_definition_id) {
                        subWFDef = data.workflow_definition.workflow_workflow_definitions[subTrackerStep.workflow_definition_id]
                    }

                    if (subWFDef) {
                        subWorkflowSteps[es[1].workflow_activity_step][subStep[1].workflow_activity_step] = {
                            executionStep: subStep[1],
                            workflowDef: subWFDef
                        };
                    }
                });
           });
        });

        let stepIdx = 0;
        _.each(data.workflow_definition.tracker_steps, function(activityStepData, idx){
            let activity = _.clone(activityStepData);

            if (!activityStepData.isDisplayedSubstep) {
                activity.step_idx = stepIdx + 1;
                stepIdx++;
            }

            let executionStep = data.execution_activity_steps[activity.id],
                stepWorkflowDef = data.workflow_definition;

            if(!executionStep){
                // see if we can find it within subworkflow steps
                let subWFStep = subWorkflowSteps[activity.parent_activity_step_id];
                if(subWFStep && subWFStep[activity.id]) {
                    executionStep = subWFStep[activity.id].executionStep;
                    stepWorkflowDef = subWFStep[activity.id].workflowDef;
                }
            }

            if(executionStep && (!stepWorkflowDef.tracker_hides_scheduled_steps || executionStep.complete))
            {
                activity.execution = executionStep;
                activity.execution.execution_id = executionStep.workflow_execution || data.id;

                activity.execution.activity_recur_count = activity.recur_count;
                activity.execution.startDisplayDate = formatDisplayDate(activity.execution.adddate);
                activity.execution.updateDisplayDate = formatDisplayDate(activity.execution.moddate);

                activity.execution.status = "pending";

                if (activity.execution.activity_execution_props)
                {
                    var props = JSON.parse(activity.execution.activity_execution_props || "{}");
                    var activity_props = JSON.parse(activity.activity_props);

                    if (props.scheduled_fire_time)
                    {
                        var fireTs = parseDate(props.scheduled_fire_time);
                        activity.execution.scheduledFireDate = formatDisplayDate(fireTs);
                        activity.execution.status_timestamp = fireTs;
                        activity.execution.status_date = activity.execution.scheduledFireDate;
                        activity.execution.tooltip = "Step Pending";
                    }

                    if (activity.activity_type === "send_message" || activity.activity_type === "send_form")
                    {
                        if (props.scheduled_fire_time){
                            activity.execution.tooltip = "Message Pending";
                            if(props.convseq || props.note_id || activity.execution.complete)
                            {
                                activity.execution.tooltip = "Message Sent";
                                activity.execution.status = "sent";
                            }
                        }
                        if (activity.execution.participant_status_info)
                        {
                            // if the target actor or one of their custodians read the message, show as viewed
                            [data.target_actor_account?.id,
                                ...(data.target_actor_account?.custodians || []).map(c=>c.custodian_id)].forEach(
                                    targetActorOrCustodianId => {
                                        let noteStatus = activity.execution.participant_status_info[targetActorOrCustodianId];
                                        if(noteStatus?.readflag){
                                            let readTs = parseDate(noteStatus.readdate);
                                            activity.execution.tooltip = "Message Opened/Viewed";
                                            activity.execution.status = "viewed";
                                            activity.execution.status_timestamp = readTs;
                                            activity.execution.status_date = formatDisplayDate(readTs);
                                        }
                                    }
                            )
                        }
                    }

                    // the logic below only works on the most recent repetition of this step
                    if (activity.activity_type === "send_form" && props.submitted_form_id)
                    {
                        activity.execution.tooltip = "Form Submitted";
                        activity.execution.status = "submitted";
                        var submitTs = parseDate(props.submitted_form_date);
                        activity.execution.status_timestamp = submitTs;
                        activity.execution.status_date = formatDisplayDate(submitTs);
                    }
                    if (activity.activity_type === "start_workflow" && activity.execution.complete) {
                        activity.execution.status = "sub_workflow_started";
                        activity.execution.tooltip = "Sub-Workflow Started";
                    }
                    if (activity.activity_type === "control_workflow" && activity.execution.complete && activity_props.workflow_control_action) {
                        activity.execution.status = activity_props.workflow_control_action;
                        let tooltip = activity_props.workflow_control_action;
                        switch (activity_props.workflow_control_action) {
                            case "restart":
                                tooltip = "Workflow Restarted";
                                break;
                            case "complete":
                                tooltip = "Workflow Completed";
                                break;
                            case "terminate":
                                tooltip = "Workflow Terminated";
                                break;
                        }
                        activity.execution.tooltip = tooltip;
                    }
                    if (activity.activity_type === "store_data") {
                        activity.execution.status = "store_data";
                        activity.execution.tooltip = "Data Stored";
                    }

                    if (activity.activity_type === "call_service") {
                        activity.execution.status = "call_service";
                        activity.execution.tooltip = "Service Called";
                    }

                    // but the last "action status" set by a user or the engine wins, regardless of repetition
                    if (activity.execution.current_action_status)
                    {
                        // override w/ any explicitly stored action status
                        activity.execution.tooltip = data.workflow_definition
                                                         ?.action_status_list
                                                         ?.find(x => x.key == activity.execution.current_action_status)
                                                         ?.label || " ";
                        activity.execution.status = activity.execution.current_action_status;
                        var statusTs = parseDate(activity.execution.moddate);
                        activity.execution.status_timestamp = statusTs;
                        activity.execution.status_date = formatDisplayDate(statusTs);
                    }

                    if (activity.execution.status)
                    {
                        activity.execution.action_status = _.findWhere(stepWorkflowDef.action_status_list,
                                                                       {key:activity.execution.status});
                    }

                    activity.execution.convseq = props["convseq"];
                    activity.execution.note_id = props["note_id"];
                    activity.execution.activity_step_id = activity.id;

                    // link to status icon, if possible
                    if (activity.execution.current_action_status && data.workflow_definition?.id) {
                        activity.execution.status_image_url =
                            "/workflows/GetStatusIcon?workflow_definition_id=" + data.workflow_definition.id + "&status=" + activity.execution.current_action_status;
                    }

                    // staff status history
                    if(props.staff_action && props.staff_action.length > 0)
                    {
                        activity.execution.staff_action_count = props.staff_action.length;
                        activity.execution.staff_action = props.staff_action;
                        _.each(activity.execution.staff_action, function(staff_activity, idx){
                            let staffAccount = _.findWhere(data.accounts || [],
                                                           {username:staff_activity.staff_username});
                            if(staffAccount)
                            {
                                staff_activity.staff_formalname = staffAccount.formalname;
                            }
                            staff_activity.display_time = formatDisplayDate(staff_activity.timestamp);

                            staff_activity.action_status = _.findWhere(stepWorkflowDef.action_status_list,
                                                                       {key:staff_activity.action_status});
                            // can edit my comments (if enabled)
                            staff_activity.is_editable_comment = (stepWorkflowDef.tracker_can_edit_comments &&
                                (staff_activity.staff_username === App.account.get("username")
                                    || stepWorkflowDef.tracker_can_edit_others_comments));
                            staff_activity.idx = idx;
                            staff_activity.id = activity.execution.id;
                        });
                        activity.execution.staff_action.reverse();
                    }

                    if(stepWorkflowDef.show_workflow_engine_status_in_history) {
                        // package up all actions
                        [
                            ["submission_actions", "Submission Action"],
                            ["delinquency_actions", "Delinquency Action"],
                            ["reply_actions", "Reply Action"],
                            ["read_actions", "Message Read Action"],
                            ["link_used_actions", "Link URL Accessed Action"]
                        ].forEach(actionInfo => {
                            let [actionType, statusReason] = actionInfo;
                            if (props[actionType] && props[actionType].length) {
                                activity.execution[actionType] = props[actionType];
                                _.each(activity.execution[actionType], function (actionActivity) {
                                    actionActivity.display_time = formatDisplayDate(actionActivity.timestamp);
                                    actionActivity.action_status = _.findWhere(stepWorkflowDef.action_status_list,
                                        {key: actionActivity.action_status});
                                    actionActivity.status_reason = statusReason;
                                });
                                activity.execution[actionType].reverse();
                            }
                        });

                        // combine all action histories that have action status changes (with *all* staff changes)
                        activity.execution.all_actions = [
                            ...(activity.execution.submission_actions || []),
                            ...(activity.execution.delinquency_actions || []),
                            ...(activity.execution.reply_actions || []),
                            ...(activity.execution.read_actions || [])]
                            .filter(action => action.action_status)
                            .concat([...(activity.execution.staff_action || [])])
                            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                        activity.execution.all_actions_count = activity.execution.all_actions.length;
                    }
                    else{
                        // by default we display staff actions only
                        activity.execution.all_actions = activity.execution.staff_action || [];
                        activity.execution.all_actions_count = activity.execution.all_actions.length;
                    }

                    activity.evaluated_conditions = props.evaluated_conditions || {};
                }
            }

            data.step_history.push(activity);
        });

    },

    formatInfoCard: function(content){
        let infoCardContent = formatNote(content);
        return replacePhoneNumbers(infoCardContent, undefined, undefined, true, App.config.get("country_codes"));
    }
});

export default WorkflowExecution;